import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "content" }
const _hoisted_2 = { class: "application-details" }
const _hoisted_3 = ["href"]
const _hoisted_4 = {
  key: 0,
  class: "mt-20"
}
const _hoisted_5 = {
  key: 1,
  class: "deployment"
}
const _hoisted_6 = { class: "simple-box-row app-instances" }
const _hoisted_7 = { class: "scale-instances" }
const _hoisted_8 = {
  key: 0,
  class: "scale-instances__spinner mt-5"
}
const _hoisted_9 = { class: "deployment__origin__row" }
const _hoisted_10 = { class: "mt-10 mb-10" }
const _hoisted_11 = {
  key: 0,
  class: "stats"
}
const _hoisted_12 = {
  key: 1,
  class: "stats-table"
}
const _hoisted_13 = { class: "mt-15" }
const _hoisted_14 = { class: "mb-10 deployment__details__header" }
const _hoisted_15 = {
  key: 0,
  class: "repo-info"
}
const _hoisted_16 = { class: "deployment__origin__list" }
const _hoisted_17 = { key: 0 }
const _hoisted_18 = ["href"]
const _hoisted_19 = { key: 1 }
const _hoisted_20 = ["href"]
const _hoisted_21 = { key: 2 }
const _hoisted_22 = { class: "sortable-table-avatar" }
const _hoisted_23 = ["src"]
const _hoisted_24 = ["href"]
const _hoisted_25 = { class: "sortable-table-commit" }
const _hoisted_26 = {
  key: 0,
  class: "icon icon-fw icon-commit"
}
const _hoisted_27 = { class: "mt-20" }

import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import day from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import Application from '../models/applications';
import SimpleBox from '@shell/components/SimpleBox.vue';
import { GitUtils } from '@shell/utils/git';
import { isArray } from '@shell/utils/array';
import ConsumptionGauge from '@shell/components/ConsumptionGauge.vue';
import { APPLICATION_MANIFEST_SOURCE_TYPE, EPINIO_TYPES } from '../types';
import DataTable from '../components/tables/DataTable.vue';
import type { DataTableColumn } from '../components/tables/types';
import BadgeStateFormatter from '@shell/components/formatter/BadgeStateFormatter.vue';
import PlusMinus from '@shell/components/form/PlusMinus.vue';
import { epinioExceptionToErrorsArray } from '../utils/errors';
import ApplicationCard from '../components/application/AppCardDetail.vue';
import Tabbed from '@shell/components/Tabbed/index.vue';
import Tab from '@shell/components/Tabbed/Tab.vue';
import AppGitDeployment from '../components/application/AppGitDeployment.vue';
import Link from '@shell/components/formatter/Link.vue';
import Banner from '@components/Banner/Banner.vue';

const UPDATE_INSTANCES_DEBOUNCE_MS = 2000; // 2s; adjust as needed

export default /*@__PURE__*/_defineComponent({
  __name: 'applications',
  props: {
    value: {},
    initialValue: {},
    mode: {}
  },
  setup(__props: any) {

day.extend(relativeTime);

const props = __props;

const store = useStore();

const t = store.getters['i18n/t'];

const scalingInFlight = ref(false);
const debouncePending = ref(false);
const gitSource = ref<any>(null);
const gitDeployment = ref({
  deployedCommit: { short: '', long: '' },
  commits: null as any
});

const instanceColumns: DataTableColumn[] = [
  {
    field: 'stateDisplay',
    label: 'State',
    width: '100px'
  },
  {
    field: 'name',
    label: 'Name'
  },
  {
    field: 'millicpus',
    label: 'Mill CPUs',
    formatter: 'milliCPUs'
  },
  {
    field: 'memoryBytes',
    label: 'RAM',
    formatter: 'memory'
  },
  {
    field: 'restarts',
    label: 'Restarts'
  },
  {
    field: 'createdAt',
    label: 'Age',
    formatter: 'age'
  }
];

const serviceColumns: DataTableColumn[] = [
  {
    field: 'stateDisplay',
    label: 'State',
    width: '100px'
  },
  {
    field: 'nameDisplay',
    label: 'Name'
  },
  {
    field: 'catalog_service',
    label: 'Catalog Service'
  },
  {
    field: 'catalog_service_version',
    label: 'Catalog Service Version'
  },
  {
    field: 'meta.createdAt',
    label: 'Age',
    formatter: 'age'
  }
];

const configColumns: DataTableColumn[] = [
  {
    field: 'nameDisplay',
    label: 'Name'
  },
  {
    field: 'variableCount',
    label: 'No. of Variables'
  },
  {
    field: 'configuration.user',
    label: 'Created By'
  },
  {
    field: 'meta.createdAt',
    label: 'Age',
    formatter: 'age'
  }
];

const commitActions = [{
  action: 'editFromCommit',
  label: t('epinio.applications.actions.editFromCommit.label'),
  icon: 'icon icon-edit',
  enabled: true
}];

// Debounce settings for scaling instances
let updateInstancesTimeout: number | null = null;

onMounted(async () => {
  await store.dispatch('epinio/findAll', { type: EPINIO_TYPES.SERVICE_INSTANCE });
  await store.dispatch('epinio/findAll', { type: EPINIO_TYPES.CONFIGURATION });

  if (props.value.appSource.git) {
    await fetchRepoDetails();
    setCommitDetails();
  }
});

async function updateInstances(newInstances: number) {
  // Update desired and configured instances immediately so the UI reflects the target
  props.value.desiredInstances = newInstances;
  props.value.configuration.instances = newInstances;

  // Debounce the API call so rapid clicks collapse into one request
  if (updateInstancesTimeout !== null) {
    clearTimeout(updateInstancesTimeout);
  }
  debouncePending.value = true;

  updateInstancesTimeout = window.setTimeout(async () => {
    debouncePending.value = false;
    scalingInFlight.value = true;

    try {
      await props.value.update();
      await props.value.forceFetch();
    } catch (err) {
      console.error('[Epinio instances] Failed to scale Application', epinioExceptionToErrorsArray(err));
    } finally {
      scalingInFlight.value = false;
      debouncePending.value = false;
      updateInstancesTimeout = null;
    }
  }, UPDATE_INSTANCES_DEBOUNCE_MS);
}

const showScaleSpinner = computed(() => debouncePending.value || scalingInFlight.value);

function formatURL(str: string) {
  const matchGit = str.match('^(https|git)(:\/\/|@)([^\/:]+)[\/:]([^\/:]+)\/(.+)(.git)*$'); // eslint-disable-line no-useless-escape
  return `${matchGit?.[4]}/${matchGit?.[5]}`;
}

async function fetchRepoDetails() {
  const { usernameOrOrg, repo } = props.value.appSource.git;
  const res = await store.dispatch(`${gitType.value}/fetchRepoDetails`, { username: usernameOrOrg, repo });

  gitSource.value = GitUtils[gitType.value].normalize.repo(res);
  await fetchCommits();
}

async function fetchCommits() {
  const { usernameOrOrg, repo, branch } = props.value.appSource.git;

  if (branch?.name) {
    gitDeployment.value.commits = await store.dispatch(`${gitType.value}/fetchCommits`, {
      username: usernameOrOrg, repo, branch
    });
  }
}

function setCommitDetails() {
  const { commit } = props.value.appSource.git;
  const selectedCommit = preparedCommits.value.find((c) => c.commitId === commit);

  gitDeployment.value.deployedCommit = {
    short: selectedCommit?.commitId?.slice(0, 7),
    long: selectedCommit.commitId
  };
}

const gitType = computed(() => props.value.appSource?.type || null);

const preparedCommits = computed(() => {
  const commits = gitDeployment.value.commits;

  if (!commits) {
    return [];
  }

  const arr = isArray(commits) ? commits : [commits];

  return arr.map((c: { sha: any; id: any; }) => ({
    ...GitUtils[gitType.value].normalize.commit(c),
    availableActions: commitActions,
    editFromCommit: () => props.value.goToEdit({ commit: c.sha || c.id })
  }));
});

const gitCommitsColumns = computed<DataTableColumn[]>(() => [
  {
    field: 'sha',
    label: t(`gitPicker.${gitType.value}.tableHeaders.sha.label`),
    width: '100px'
  },
  {
    field: 'author_login',
    label: t(`gitPicker.${gitType.value}.tableHeaders.author.label`),
    width: '190px'
  },
  {
    field: 'message',
    label: t(`gitPicker.${gitType.value}.tableHeaders.message.label`)
  },
  {
    field: 'date',
    label: t(`gitPicker.${gitType.value}.tableHeaders.date.label`),
    width: '220px',
    formatter: 'dateTime'
  }
]);

const sourceIcon = computed(() => props.value.appSourceInfo?.icon || 'icon-epinio');

const commitPosition = computed(() => {
  if (!preparedCommits.value.length && !gitDeployment.value.deployedCommit) {
    return null;
  }

  let idx: number | null = null;

  preparedCommits.value.forEach((ele: { commitId: string; }, i: number) => {
    if (ele.commitId === gitDeployment.value?.deployedCommit?.long) {
      idx = i - 1;
    }
  });

  if (idx === null || idx < 0) {
    return {
      text: t('epinio.applications.gitSource.latestCommit'),
      position: 0
    };
  }

  return {
    text: `${idx} ${t('epinio.applications.gitSource.behindCommits')}`,
    position: idx
  };
});

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(ApplicationCard, null, {
        cardIcon: _withCtx(() => [
          _createElementVNode("i", {
            class: _normalizeClass(["icon icon-fw", sourceIcon.value])
          }, null, 2)
        ]),
        "top-left": _withCtx(() => [
          _cache[3] || (_cache[3] = _createElementVNode("h1", null, "Routes", -1)),
          _cache[4] || (_cache[4] = _createTextVNode()),
          _createElementVNode("ul", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.value.configuration.routes, (route) => {
              return (_openBlock(), _createElementBlock("li", {
                key: route.id
              }, [
                (_ctx.value.state === 'running')
                  ? (_openBlock(), _createElementBlock("a", {
                      key: route.id + 'a',
                      href: `https://${route}`,
                      target: "_blank",
                      rel: "noopener noreferrer nofollow"
                    }, _toDisplayString(`https://${route}`), 9, _hoisted_3))
                  : (_openBlock(), _createElementBlock("span", {
                      key: route.id + 'b'
                    }, _toDisplayString(`https://${route}`), 1))
              ]))
            }), 128))
          ])
        ]),
        resourcesCount: _withCtx(() => [
          _createElementVNode("div", null, _toDisplayString(_ctx.value.envCount) + " " + _toDisplayString(_unref(t)('epinio.applications.detail.counts.envVars')), 1),
          _cache[5] || (_cache[5] = _createTextVNode()),
          _createElementVNode("div", null, _toDisplayString(_ctx.value.serviceConfigurations.length) + " " + _toDisplayString(_unref(t)('epinio.applications.detail.counts.services')), 1),
          _cache[6] || (_cache[6] = _createTextVNode()),
          _createElementVNode("div", null, _toDisplayString(_ctx.value.baseConfigurations.length) + " " + _toDisplayString(_unref(t)('epinio.applications.detail.counts.config')), 1)
        ]),
        _: 1,
        __: [7,8]
      })
    ]),
    _cache[56] || (_cache[56] = _createTextVNode()),
    (_ctx.value.deployment)
      ? (_openBlock(), _createElementBlock("h3", _hoisted_4, _toDisplayString(_unref(t)('epinio.applications.detail.deployment.label')), 1))
      : _createCommentVNode("", true),
    _cache[57] || (_cache[57] = _createTextVNode()),
    (_ctx.value.deployment)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(Tabbed, null, {
            default: _withCtx(() => [
              _createVNode(Tab, {
                "label-key": "epinio.applications.detail.tables.overview",
                name: "overview",
                weight: 3
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(SimpleBox, null, {
                      default: _withCtx(() => [
                        _createVNode(ConsumptionGauge, {
                          "resource-name": _unref(t)('epinio.applications.detail.deployment.instances'),
                          capacity: _ctx.value.desiredInstances,
                          used: _ctx.value.readyInstances,
                          "used-as-resource-name": true,
                          "color-stops": { 70: '--success', 30: '--warning', 0: '--error' }
                        }, null, 8, ["resource-name", "capacity", "used"]),
                        _cache[36] || (_cache[36] = _createTextVNode()),
                        _createElementVNode("div", _hoisted_7, [
                          _createVNode(PlusMinus, {
                            value: _ctx.value.desiredInstances,
                            "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((_ctx.value.desiredInstances) = $event)),
                            class: "mt-15 mb-10",
                            disabled: scalingInFlight.value,
                            onMinus: _cache[1] || (_cache[1] = ($event: any) => (updateInstances(_ctx.value.desiredInstances - 1))),
                            onPlus: _cache[2] || (_cache[2] = ($event: any) => (updateInstances(_ctx.value.desiredInstances + 1)))
                          }, null, 8, ["value", "disabled"]),
                          _cache[10] || (_cache[10] = _createTextVNode()),
                          (showScaleSpinner.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, _cache[9] || (_cache[9] = [
                                _createElementVNode("i", { class: "icon-spinner animate-spin" }, null, -1)
                              ])))
                            : _createCommentVNode("", true)
                        ]),
                        _cache[37] || (_cache[37] = _createTextVNode()),
                        _createElementVNode("div", _hoisted_9, [
                          _cache[33] || (_cache[33] = _createElementVNode("hr", { class: "mt-10 mb-10" }, null, -1)),
                          _cache[34] || (_cache[34] = _createTextVNode()),
                          _createElementVNode("h4", _hoisted_10, _toDisplayString(_unref(t)('epinio.applications.detail.deployment.metrics')), 1),
                          _cache[35] || (_cache[35] = _createTextVNode()),
                          (gitSource.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                _createElementVNode("div", null, [
                                  _createElementVNode("h3", null, _toDisplayString(_unref(t)('tableHeaders.memory')), 1),
                                  _cache[16] || (_cache[16] = _createTextVNode()),
                                  _createElementVNode("ul", null, [
                                    _createElementVNode("li", null, [
                                      _cache[11] || (_cache[11] = _createElementVNode("span", null, "Min: ", -1)),
                                      _createTextVNode(" " + _toDisplayString(_ctx.value.instanceMemory.min), 1)
                                    ]),
                                    _cache[14] || (_cache[14] = _createTextVNode()),
                                    _createElementVNode("li", null, [
                                      _cache[12] || (_cache[12] = _createElementVNode("span", null, "Max: ", -1)),
                                      _createTextVNode(_toDisplayString(_ctx.value.instanceMemory.max), 1)
                                    ]),
                                    _cache[15] || (_cache[15] = _createTextVNode()),
                                    _createElementVNode("li", null, [
                                      _cache[13] || (_cache[13] = _createElementVNode("span", null, "Avg: ", -1)),
                                      _createTextVNode(_toDisplayString(_ctx.value.instanceMemory.avg), 1)
                                    ])
                                  ])
                                ]),
                                _cache[23] || (_cache[23] = _createTextVNode()),
                                _createElementVNode("div", null, [
                                  _createElementVNode("h3", null, _toDisplayString(_unref(t)('tableHeaders.cpu')), 1),
                                  _cache[22] || (_cache[22] = _createTextVNode()),
                                  _createElementVNode("ul", null, [
                                    _createElementVNode("li", null, [
                                      _cache[17] || (_cache[17] = _createElementVNode("span", null, "Min: ", -1)),
                                      _createTextVNode(" " + _toDisplayString(_ctx.value.instanceCpu.min), 1)
                                    ]),
                                    _cache[20] || (_cache[20] = _createTextVNode()),
                                    _createElementVNode("li", null, [
                                      _cache[18] || (_cache[18] = _createElementVNode("span", null, "Max: ", -1)),
                                      _createTextVNode(_toDisplayString(_ctx.value.instanceCpu.max), 1)
                                    ]),
                                    _cache[21] || (_cache[21] = _createTextVNode()),
                                    _createElementVNode("li", null, [
                                      _cache[19] || (_cache[19] = _createElementVNode("span", null, "Avg: ", -1)),
                                      _createTextVNode(_toDisplayString(_ctx.value.instanceCpu.avg), 1)
                                    ])
                                  ])
                                ])
                              ]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                _createElementVNode("table", _hoisted_13, [
                                  _cache[31] || (_cache[31] = _createElementVNode("thead", null, [
                                    _createElementVNode("tr", null, [
                                      _createElementVNode("th"),
                                      _createTextVNode(),
                                      _createElementVNode("th", null, "Min"),
                                      _createTextVNode(),
                                      _createElementVNode("th", null, "Max"),
                                      _createTextVNode(),
                                      _createElementVNode("th", null, "Avg")
                                    ])
                                  ], -1)),
                                  _cache[32] || (_cache[32] = _createTextVNode()),
                                  _createElementVNode("tbody", null, [
                                    _createElementVNode("tr", null, [
                                      _createElementVNode("td", null, _toDisplayString(_unref(t)('tableHeaders.memory')), 1),
                                      _cache[24] || (_cache[24] = _createTextVNode()),
                                      _createElementVNode("td", null, _toDisplayString(_ctx.value.instanceMemory.min), 1),
                                      _cache[25] || (_cache[25] = _createTextVNode()),
                                      _createElementVNode("td", null, _toDisplayString(_ctx.value.instanceMemory.max), 1),
                                      _cache[26] || (_cache[26] = _createTextVNode()),
                                      _createElementVNode("td", null, _toDisplayString(_ctx.value.instanceMemory.avg), 1)
                                    ]),
                                    _cache[30] || (_cache[30] = _createTextVNode()),
                                    _createElementVNode("tr", null, [
                                      _createElementVNode("td", null, _toDisplayString(_unref(t)('tableHeaders.cpu')), 1),
                                      _cache[27] || (_cache[27] = _createTextVNode()),
                                      _createElementVNode("td", null, _toDisplayString(_ctx.value.instanceCpu.min), 1),
                                      _cache[28] || (_cache[28] = _createTextVNode()),
                                      _createElementVNode("td", null, _toDisplayString(_ctx.value.instanceCpu.max), 1),
                                      _cache[29] || (_cache[29] = _createTextVNode()),
                                      _createElementVNode("td", null, _toDisplayString(_ctx.value.instanceCpu.avg), 1)
                                    ])
                                  ])
                                ])
                              ]))
                        ])
                      ]),
                      _: 1,
                      __: [36,37]
                    }),
                    _cache[48] || (_cache[48] = _createTextVNode()),
                    (_ctx.value.appSourceInfo)
                      ? (_openBlock(), _createBlock(SimpleBox, { key: 0 }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_14, [
                              (_ctx.value.appSourceInfo.kind === _unref(APPLICATION_MANIFEST_SOURCE_TYPE).GIT)
                                ? (_openBlock(), _createElementBlock("i", {
                                    key: 0,
                                    class: _normalizeClass(["icon git-icon", {[`icon-${gitType.value}`]: true}])
                                  }, null, 2))
                                : _createCommentVNode("", true),
                              _cache[38] || (_cache[38] = _createTextVNode()),
                              _createElementVNode("h4", null, _toDisplayString(_unref(t)('epinio.applications.detail.deployment.details.label')), 1)
                            ]),
                            _cache[44] || (_cache[44] = _createTextVNode()),
                            (gitSource.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                  _createVNode(AppGitDeployment, {
                                    "git-deployment": gitDeployment.value,
                                    "git-source": gitSource.value,
                                    "commit-position": commitPosition.value
                                  }, null, 8, ["git-deployment", "git-source", "commit-position"])
                                ]))
                              : _createCommentVNode("", true),
                            _cache[45] || (_cache[45] = _createTextVNode()),
                            _cache[46] || (_cache[46] = _createElementVNode("hr", { class: "mt-10 mb-10" }, null, -1)),
                            _cache[47] || (_cache[47] = _createTextVNode()),
                            _createElementVNode("div", _hoisted_16, [
                              _createElementVNode("ul", null, [
                                _createElementVNode("li", null, [
                                  _createElementVNode("h4", null, _toDisplayString(_unref(t)('epinio.applications.detail.deployment.details.origin')), 1),
                                  _cache[39] || (_cache[39] = _createTextVNode()),
                                  _createElementVNode("span", null, _toDisplayString(_ctx.value.appSourceInfo.label), 1)
                                ]),
                                _cache[42] || (_cache[42] = _createTextVNode()),
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.value.appSourceInfo.details, (d) => {
                                  return (_openBlock(), _createElementBlock("li", {
                                    key: d.label
                                  }, [
                                    _createElementVNode("h4", null, _toDisplayString(d.label), 1),
                                    _cache[40] || (_cache[40] = _createTextVNode()),
                                    (d.value && d.value.startsWith('http'))
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_17, [
                                          _createElementVNode("a", {
                                            href: d.value,
                                            target: "_blank"
                                          }, _toDisplayString(formatURL(d.value)), 9, _hoisted_18)
                                        ]))
                                      : (gitSource.value && d.value && d.value.match(/^[a-f0-9]{40}$/))
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_19, [
                                            _createElementVNode("a", {
                                              href: `${gitSource.value.htmlUrl}/commit/${d.value}`,
                                              target: "_blank"
                                            }, _toDisplayString(d.value), 9, _hoisted_20)
                                          ]))
                                        : (_openBlock(), _createElementBlock("span", _hoisted_21, _toDisplayString(d.value), 1))
                                  ]))
                                }), 128)),
                                _cache[43] || (_cache[43] = _createTextVNode()),
                                _createElementVNode("li", null, [
                                  _createElementVNode("h4", null, _toDisplayString(_unref(t)('epinio.applications.tableHeaders.deployedBy')), 1),
                                  _cache[41] || (_cache[41] = _createTextVNode()),
                                  _createElementVNode("span", null, _toDisplayString(_ctx.value.deployment.username), 1)
                                ])
                              ])
                            ])
                          ]),
                          _: 1,
                          __: [44,45,46,47]
                        }))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _: 1
              }),
              _cache[53] || (_cache[53] = _createTextVNode()),
              (gitSource.value && preparedCommits.value.length)
                ? (_openBlock(), _createBlock(Tab, {
                    key: 0,
                    "label-key": "epinio.applications.detail.tables.gitCommits",
                    name: "gitCommits",
                    weight: 2
                  }, {
                    default: _withCtx(() => [
                      _createVNode(Banner, {
                        color: "info",
                        class: "redeploy-info"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)('epinio.applications.detail.deployment.commits.redeploy')), 1)
                        ]),
                        _: 1
                      }),
                      _cache[52] || (_cache[52] = _createTextVNode()),
                      (preparedCommits.value)
                        ? (_openBlock(), _createBlock(DataTable, {
                            key: 0,
                            rows: preparedCommits.value,
                            columns: gitCommitsColumns.value,
                            "key-field": "sha",
                            searchable: true,
                            paginated: true,
                            "rows-per-page": 10
                          }, {
                            "cell:author_login": _withCtx(({row}) => [
                              _createElementVNode("div", _hoisted_22, [
                                (row.author)
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      _createElementVNode("img", {
                                        src: row.author.avatarUrl,
                                        alt: ""
                                      }, null, 8, _hoisted_23),
                                      _cache[49] || (_cache[49] = _createTextVNode()),
                                      _createElementVNode("a", {
                                        href: row.author.htmlUrl,
                                        target: "_blank",
                                        rel: "nofollow noopener noreferrer"
                                      }, _toDisplayString(row.author.name), 9, _hoisted_24)
                                    ], 64))
                                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                      _createTextVNode(_toDisplayString(_unref(t)(`gitPicker.${ gitType.value }.tableHeaders.author.unknown`)), 1)
                                    ], 64))
                              ])
                            ]),
                            "cell:sha": _withCtx(({row}) => [
                              _createElementVNode("div", _hoisted_25, [
                                _createVNode(Link, {
                                  value: row.sha,
                                  "onUpdate:value": ($event: any) => ((row.sha) = $event),
                                  row: row,
                                  "url-key": "htmlUrl"
                                }, null, 8, ["value", "onUpdate:value", "row"]),
                                _cache[50] || (_cache[50] = _createTextVNode()),
                                (row.commitId === gitDeployment.value.deployedCommit.long)
                                  ? _withDirectives((_openBlock(), _createElementBlock("i", _hoisted_26, null, 512)), [
                                      [_directive_tooltip, _unref(t)('epinio.applications.detail.deployment.details.git.deployed')]
                                    ])
                                  : _createCommentVNode("", true)
                              ])
                            ]),
                            _: 1,
                            __: [51]
                          }, 8, ["rows", "columns"]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1,
                    __: [52]
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1,
            __: [53]
          })
        ]))
      : _createCommentVNode("", true),
    _cache[58] || (_cache[58] = _createTextVNode()),
    _createElementVNode("h3", _hoisted_27, _toDisplayString(_unref(t)('epinio.applications.detail.tables.label')), 1),
    _cache[59] || (_cache[59] = _createTextVNode()),
    _createElementVNode("div", null, [
      _createVNode(Tabbed, null, {
        default: _withCtx(() => [
          _createVNode(Tab, {
            "label-key": "epinio.applications.detail.tables.instances",
            name: "instances",
            weight: 3
          }, {
            default: _withCtx(() => [
              _createVNode(DataTable, {
                columns: instanceColumns,
                rows: _ctx.value.instances,
                searchable: false,
                paginated: false
              }, {
                "cell:stateDisplay": _withCtx(({ row }) => [
                  _createVNode(BadgeStateFormatter, {
                    row: row,
                    value: row.stateDisplay
                  }, null, 8, ["row", "value"])
                ]),
                _: 1
              }, 8, ["rows"])
            ]),
            _: 1
          }),
          _cache[54] || (_cache[54] = _createTextVNode()),
          _createVNode(Tab, {
            "label-key": "epinio.applications.detail.tables.services",
            name: "services",
            weight: 2
          }, {
            default: _withCtx(() => [
              _createVNode(DataTable, {
                columns: serviceColumns,
                rows: _ctx.value.services,
                searchable: false,
                paginated: false
              }, {
                "cell:stateDisplay": _withCtx(({ row }) => [
                  _createVNode(BadgeStateFormatter, {
                    row: row,
                    value: row.stateDisplay
                  }, null, 8, ["row", "value"])
                ]),
                _: 1
              }, 8, ["rows"])
            ]),
            _: 1
          }),
          _cache[55] || (_cache[55] = _createTextVNode()),
          _createVNode(Tab, {
            "label-key": "epinio.applications.detail.tables.configs",
            name: "configs",
            weight: 1
          }, {
            default: _withCtx(() => [
              _createVNode(DataTable, {
                columns: configColumns,
                rows: _ctx.value.baseConfigurations,
                searchable: false,
                paginated: false
              }, {
                "cell:stateDisplay": _withCtx(({ row }) => [
                  _createVNode(BadgeStateFormatter, {
                    row: row,
                    value: row.stateDisplay
                  }, null, 8, ["row", "value"])
                ]),
                _: 1
              }, 8, ["rows"])
            ]),
            _: 1
          })
        ]),
        _: 1,
        __: [54,55]
      })
    ])
  ]))
}
}

})